import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowClockwise,
    ClipboardText,
    MagnifyingGlass,
    Package,
    Plus,
    Tag,
    TaskDaily,
    Users,
    WarningCircle,
} from 'shared/icons';
import { InspectionStation } from '../components/rma/inspectionStation';
import {
    ComputeTracks,
    dueInfo,
    fmtDate,
    HasPhysicalFlow,
    NearestDue,
    PrintRmaLabels,
    REFUND_LABELS,
    RESOLUTION_LABELS,
    RecontactOurTurn,
    rmaIdentifier,
    SituationLine,
    STATUS_META,
    TrackChips,
} from '../components/rma/rmaShared';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Modal from '../components/ui/Modal';
import Select from '../components/ui/Select';
import Textarea from '../components/ui/Textarea';
import { useToast } from '../components/ui/Toast';
import { ML_CLAIM_TYPE_LABELS, MlLabel } from '../constants/mlClaimCatalog';
import { IsReturnToUs, NeedsCaseReconcile } from '../constants/pedidoTags';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';

/**
 * RMAs — fila de trabalho por papel (redesign 2026-08-08).
 *
 * Estrutura: coluna esquerda = A FILA do papel escolhido (obrigações como
 * itens de navegação, com contagem); área principal = a lista da fila
 * selecionada, com linhas calmas (faixa de cor do estado + pill + tags só
 * para o excepcional). O operador escolhe o papel no topo e a tela diz o que
 * ele precisa fazer, em ordem.
 *
 * Para explorar o design só com registros de teste, troque DEMO_SOURCE para
 * 'MANUAL' (filtra lista e contadores por origem).
 */
const DEMO_SOURCE = null;

const OPERATOR_ROLES = {
    supervisor: 'Supervisor — visão geral',
    atendimento: 'Atendimento',
    logistica: 'Logística',
    financeiro: 'Financeiro',
};

// Filas de trabalho — as raias do fluxograma do atendimento (2026-08-08).
// Cada uma é um FILTRO nomeado; a contagem vem de /rmas/tasks e usa o MESMO
// predicado do filtro (badge e lista sempre batem). As filas do supervisor
// espelham pontos das equipes de propósito: são as janelas de intervenção dele.
const QUEUE_DEFS = [
    // — Supervisor: acompanhamento e intervenção —
    {
        key: 'mediacoes_abertas',
        group: 'supervisor',
        label: 'Acompanhar mediações',
        // Recontato entra: o mediador também participa do caso reaberto.
        filter: { mediation_status: 'EM_MEDIACAO,RECONTATO', order_by: 'mediation_due' },
    },
    {
        key: 'rma_fabricante',
        group: 'supervisor',
        label: 'RMAs com fabricante',
        filter: { supplier_concluded: 'false' },
    },
    {
        key: 'reembolsos_conciliar',
        group: 'supervisor',
        label: 'Conferir reembolsos',
        filter: { refund_pending_reconcile: 'true' },
    },
    {
        key: 'devolucoes_nao_entregues',
        group: 'supervisor',
        label: 'Devoluções não entregues',
        urgent: true,
        // Devolução falhou vindo pra nós: conferir rastreio + conciliação; se o
        // valor foi debitado sem a mercadoria chegar, acionar o suporte do ML.
        filter: { return_failed: 'true' },
    },
    // — Atendimento —
    {
        key: 'reclamacoes_responder',
        group: 'atendimento',
        label: 'Responder reclamações',
        urgent: true,
        filter: { claim_pending_response: 'true', order_by: 'mediation_due' },
    },
    {
        key: 'revisar_devolucao',
        group: 'atendimento',
        label: 'Revisar devolução (veredito ML)',
        urgent: true,
        filter: { review_pending: 'true', order_by: 'mediation_due' },
    },
    {
        key: 'mediacoes_responder',
        group: 'atendimento',
        label: 'Responder mediações',
        urgent: true,
        filter: { mediation_status: 'EM_MEDIACAO,RECONTATO', pending_response: 'true', order_by: 'mediation_due' },
    },
    {
        key: 'recontatos_responder',
        group: 'atendimento',
        label: 'Recontatos (casos reabertos)',
        urgent: true,
        // O comprador (ou o ML) voltou a falar DEPOIS do encerramento — o caso
        // reabriu como recontato e espera resposta nossa.
        filter: { recontact_pending: 'true', order_by: 'mediation_due' },
    },
    {
        key: 'mediacoes_vencendo',
        group: 'atendimento',
        label: 'Mediações vencendo (48h)',
        filter: {
            mediation_status: 'EM_MEDIACAO,RECONTATO',
            mediation_due_within_hours: 48,
            order_by: 'mediation_due',
        },
    },
    {
        key: 'para_inspecionar',
        group: 'atendimento',
        label: 'Inspecionar mercadorias',
        urgent: true,
        filter: { status: 'RECEBIDO' },
    },
    {
        key: 'nf_entrada_pendente',
        group: 'atendimento',
        label: 'NF de entrada pendente',
        filter: { nf_entry_pending: 'true' },
    },
    {
        key: 'avaliar_fornecedor',
        group: 'atendimento',
        label: 'Avaria — avaliar fornecedor',
        // Fila pelo nome: o destino RMA do supervisor manda; a avaria só na
        // inspeção antiga (o mesmo predicado da contagem).
        filter: { queue: 'avaliar_fornecedor' },
    },
    {
        key: 'etiquetas_pendentes',
        group: 'atendimento',
        label: 'Associar etiquetas',
        tab: 'etiquetas',
    },
    {
        key: 'enviar_fabricante',
        group: 'atendimento',
        label: 'Cadastrar no fabricante',
        filter: {
            status: 'RESOLVIDO',
            resolution: 'DEVOLUCAO_FORNECEDOR,GARANTIA_FABRICANTE',
            supplier_registered: 'false',
        },
    },
    {
        key: 'fabricante_despachar',
        group: 'atendimento',
        label: 'Despachar ao fabricante',
        filter: { supplier_stage: 'CADASTRADO' },
    },
    {
        key: 'fabricante_retorno',
        group: 'atendimento',
        label: 'Fabricante — aguardando retorno',
        filter: { supplier_stage: 'DESPACHADO' },
    },
    // — Logística: imprimir etiquetas, receber, devolver ao estoque —
    {
        key: 'imprimir_etiquetas',
        group: 'logistica',
        label: 'Imprimir etiquetas',
        tab: 'etiquetas',
    },
    {
        key: 'para_receber',
        group: 'logistica',
        label: 'Devoluções a caminho',
        // Mesmo predicado do badge (RmaQueuePredicates.para_receber): em
        // trânsito, vindo para o NOSSO endereço — Full inclusive (24/09).
        filter: { queue: 'para_receber' },
    },
    {
        key: 'voltar_estoque',
        group: 'logistica',
        label: 'Devolver ao estoque',
        // Fila pelo nome: o destino ESTOQUE do supervisor manda; sem avaria
        // só na inspeção antiga (o mesmo predicado da contagem).
        filter: { queue: 'voltar_estoque' },
    },
    // — Financeiro —
    {
        key: 'conferir_reembolso',
        group: 'financeiro',
        label: 'Conciliar reembolsos',
        urgent: true,
        filter: { refund_pending_reconcile: 'true' },
    },
    {
        key: 'nf_perda_pendente',
        group: 'financeiro',
        label: 'Emitir NF de perda',
        filter: { nf_loss_pending: 'true' },
    },
    // — Devoluções por categoria de motivo (navegação, não pendência) —
    // O item "geral" abre/fecha as variações (pedido do time 2026-08-19).
    {
        key: 'devolucoes_geral',
        group: 'devolucoes',
        label: 'Devoluções (geral)',
        filter: { has_return: 'true' },
    },
    { key: 'dev_defeito', group: 'devolucoes', label: 'Com defeito', filter: { reason_group: 'defeito' } },
    { key: 'dev_avaria', group: 'devolucoes', label: 'Avaria / embalagem', filter: { reason_group: 'avaria' } },
    {
        key: 'dev_produto_diferente',
        group: 'devolucoes',
        label: 'Produto diferente',
        filter: { reason_group: 'produto_diferente' },
    },
    {
        key: 'dev_incompleto',
        group: 'devolucoes',
        label: 'Incompleto / faltando',
        filter: { reason_group: 'incompleto' },
    },
    {
        key: 'dev_arrependimento',
        group: 'devolucoes',
        label: 'Arrependimento',
        filter: { reason_group: 'arrependimento' },
    },
    { key: 'dev_nao_recebido', group: 'devolucoes', label: 'Não recebido', filter: { reason_group: 'nao_recebido' } },
    { key: 'dev_outros', group: 'devolucoes', label: 'Outros motivos', filter: { reason_group: 'outros' } },
];

// ── Processo do dia (visão BPM) ──────────────────────────────────────────────
// O fluxo do pós-venda em etapas ORDENADAS, cada uma com o dono (a "responsa")
// e a contagem do que está parado nela agora. Clicar abre a fila da etapa.
const TEAM_META = {
    atendimento: { label: 'Atendimento', chip: 'bg-sky-50 text-sky-700 dark:bg-sky-400/10 dark:text-sky-300' },
    logistica: { label: 'Logística', chip: 'bg-violet-50 text-violet-700 dark:bg-violet-400/10 dark:text-violet-300' },
    financeiro: { label: 'Financeiro', chip: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-400/10 dark:text-cyan-300' },
};

const PROCESS_FLOW = [
    { key: 'reclamacoes_responder', label: 'Responder reclamações', owner: 'atendimento' },
    { key: 'mediacoes_responder', label: 'Responder mediações', owner: 'atendimento' },
    { key: 'recontatos_responder', label: 'Responder recontatos', owner: 'atendimento' },
    { key: 'revisar_devolucao', label: 'Revisar devolução', owner: 'atendimento' },
    { key: 'para_receber', label: 'Devolução a caminho', owner: 'logistica' },
    // Logística GERA e COLA a etiqueta em tudo que chega; só depois o
    // atendimento IDENTIFICA (associa a etiqueta colada à venda).
    {
        key: 'imprimir_etiquetas',
        label: 'Gerar e colar etiquetas',
        owner: 'logistica',
        tab: 'etiquetas',
        noCount: true,
    },
    { key: 'etiquetas_pendentes', label: 'Identificar mercadorias', owner: 'atendimento', tab: 'etiquetas' },
    { key: 'para_inspecionar', label: 'Inspecionar', owner: 'atendimento' },
    { key: 'nf_entrada_pendente', label: 'Dar entrada (NF)', owner: 'atendimento' },
    { key: 'voltar_estoque', label: 'Devolver ao estoque', owner: 'logistica' },
    {
        key: 'fabricante',
        label: 'RMA fabricante',
        owner: 'atendimento',
        composite: ['enviar_fabricante', 'fabricante_despachar', 'fabricante_retorno'],
        target: 'enviar_fabricante',
    },
    { key: 'conferir_reembolso', label: 'Conciliar reembolso', owner: 'financeiro' },
    { key: 'nf_perda_pendente', label: 'NF de perda', owner: 'financeiro' },
];

const ProcessFlow = ({ tasks, role, onSelect }) => {
    const countOf = (key) => {
        const def = QUEUE_DEFS.find((d) => d.key === key);
        return def ? (tasks?.[def.group]?.[def.key] ?? 0) : 0;
    };
    // Cada operador vê SÓ as etapas dele; o supervisor vê o fluxo inteiro
    // (com o chip de quem é a responsa em cada uma).
    const visibleNodes = PROCESS_FLOW.filter((node) => role === 'supervisor' || node.owner === role);
    return (
        <div className="overflow-x-auto border border-mono-200 bg-white p-4 shadow-sm-mono dark:border-mono-800 dark:bg-mono-950">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                {role === 'supervisor'
                    ? 'Processo do dia — de quem é a responsa em cada etapa'
                    : `Processo do dia — suas etapas (${TEAM_META[role]?.label || role})`}
            </p>
            <div className="flex items-stretch gap-1">
                {visibleNodes.map((node, i) => {
                    const count = node.noCount
                        ? null
                        : node.composite
                          ? node.composite.reduce((acc, k) => acc + countOf(k), 0)
                          : countOf(node.key);
                    const team = TEAM_META[node.owner];
                    return (
                        <div key={node.key} className="flex items-center gap-1">
                            {i > 0 && <span className="shrink-0 text-mono-300 dark:text-mono-700">→</span>}
                            <button
                                type="button"
                                onClick={() => onSelect(node.tab || node.target || node.key)}
                                className={`flex w-[7.5rem] shrink-0 flex-col items-start gap-1 border px-3 py-2 text-left transition-all hover:border-mono-500 dark:hover:border-mono-400 ${
                                    count === null || count > 0
                                        ? 'border-mono-300 dark:border-mono-700'
                                        : 'border-mono-100 dark:border-mono-900'
                                }`}
                            >
                                <span
                                    className={`text-xl font-bold tabular-nums leading-none ${
                                        count > 0 ? 'text-black dark:text-white' : 'text-mono-300 dark:text-mono-700'
                                    }`}
                                >
                                    {count === null ? '·' : count}
                                </span>
                                <span className="text-[11px] font-medium leading-tight text-mono-600 dark:text-mono-300">
                                    {node.label}
                                </span>
                                {role === 'supervisor' && (
                                    <span
                                        className={` px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${team.chip}`}
                                    >
                                        {team.label}
                                    </span>
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Item da fila (navegação lateral) ─────────────────────────────────────────
// ── Caixa de entrada (redesign 2026-08-26) ───────────────────────────────────
// UMA fila priorizada por papel: a união das filas de AÇÃO daquele papel,
// classificada client-side pelos MESMOS campos que alimentam as filas do
// servidor (QUEUE_DEFS) — mudou uma fila lá, mude a regra aqui.

const OPEN_PIPELINE = ['ABERTO', 'EM_TRANSITO', 'RECEBIDO', 'INSPECIONADO'];
const FABRICANTE_RESOLUTIONS = ['DEVOLUCAO_FORNECEDOR', 'GARANTIA_FABRICANTE'];
const FAILED_RETURN = ['cancelled', 'not_delivered', 'expired', 'failed'];

// Fila do caso INSPECIONADO ainda sem desfecho — espelha voltar_estoque /
// avaliar_fornecedor de RmaQueuePredicates: o DESTINO definido pelo
// supervisor manda (ESTOQUE → estoque, RMA → fornecedor, DESCARTE → nenhuma);
// sem destino, a avaria decide SÓ na inspeção antiga (sem checklist) — caso de
// checklist sem destino espera o supervisor.
function inspectedQueue(rma) {
    if (rma.status !== 'INSPECIONADO' || rma.resolution) {
        return null;
    }
    if (rma.inspection_destination) {
        return { ESTOQUE: 'voltar_estoque', RMA: 'avaliar_fornecedor' }[rma.inspection_destination] || null;
    }
    if (rma.inspection_checklist_version !== null && rma.inspection_checklist_version !== undefined) {
        return null;
    }
    if (rma.inspection_damage === true) {
        return 'avaliar_fornecedor';
    }
    return rma.inspection_damage === false ? 'voltar_estoque' : null;
}

// Por que esta ocorrência está na SUA caixa? null = não está (vai para
// "Aguardando" se ainda estiver aberta).
function InboxReason(rma, role) {
    const destinationOurs = !rma.return_destination || rma.return_destination === 'seller_address';
    const refunded = ['REEMBOLSADO', 'PARCIAL'].includes(rma.refund_status);
    // Janela viva (espelha o liveWindow do servidor): caso fechado só conta
    // enquanto o prazo da ação obrigatória não venceu — o sync não revisita
    // fechados, então sem isto a urgência ficava eterna.
    const live =
        !rma.ml_claim_status ||
        rma.ml_claim_status === 'opened' ||
        (rma.ml_mandatory_due && new Date(rma.ml_mandatory_due) >= new Date());
    // Chegada ao CD (rodada 7 — espelha `arrivedOurs` de
    // RmaQueuePredicates.revisar_devolucao): o ML entregou a devolução vindo
    // para nós (`IsReturnToUs` = returnToUs) ou a Logística já a recebeu
    // (etiqueta ASSOCIADA, status RECEBIDO/INSPECIONADO).
    const arrived =
        ['RECEBIDO', 'INSPECIONADO'].includes(rma.status) ||
        rma.has_label === true ||
        (rma.return_shipment_status === 'delivered' && IsReturnToUs(rma));
    if (role === 'atendimento') {
        // Veredito ao ML (parte da inspeção): só depois da chegada — em
        // trânsito ou indo ao armazém do ML não acende.
        if (live && arrived && (rma.ml_pending_actions || []).some((a) => String(a).startsWith('return_review'))) {
            return { urgent: true };
        }
        // Recontato: só com a vez NOSSA (`RecontactOurTurn` — regra da fila
        // recontatos_responder); em análise pelo ML não é tarefa de ninguém.
        if (RecontactOurTurn(rma) || (rma.mediation_status !== 'RECONTATO' && live && rma.ml_mandatory_action)) {
            return { urgent: true };
        }
        // Inspeção SÓ com o caso RECEBIDO (rodada 7): a Logística etiqueta
        // (= recebe) antes; entregue e sem etiqueta ainda não é do Atendimento.
        if (rma.status === 'RECEBIDO') {
            return { urgent: true };
        }
        if (inspectedQueue(rma) === 'avaliar_fornecedor') {
            return {};
        }
        // NF-e de devolução: só depois da INSPEÇÃO no CD e quando o ML
        // confirmadamente não emitiu (espelha nf_entrada_pendente).
        if (
            rma.inspected_at &&
            !rma.nf_entry_at &&
            rma.return_id &&
            HasPhysicalFlow(rma) &&
            rma.ml_return_invoice_checked_at &&
            !rma.ml_return_invoice
        ) {
            return {};
        }
        if (FABRICANTE_RESOLUTIONS.includes(rma.resolution) && !rma.supplier_shipped_at) {
            return {};
        }
        return null;
    }
    if (role === 'logistica') {
        // A Logística etiqueta (= recebe) o que chegou, e cuida de etiquetas e
        // reestoque. Chegou ao CD e está sem etiqueta = etiquetar (espelha
        // devolucoes_etiquetar / tag devolucao_para_etiquetar).
        if (
            ['ABERTO', 'EM_TRANSITO'].includes(rma.status) &&
            rma.has_label !== true &&
            HasPhysicalFlow(rma) &&
            rma.return_shipment_status === 'delivered' &&
            IsReturnToUs(rma)
        ) {
            return { urgent: true };
        }
        if (inspectedQueue(rma) === 'voltar_estoque') {
            return {};
        }
        return null;
    }
    if (role === 'financeiro') {
        // NF de perda (rodada 7 — mesma regra de fiscal.nf_perda_pendente):
        // a INSPEÇÃO apontou problema ("chegou errado") OU o supervisor
        // definiu o destino DESCARTE (ajuste 2 do dono, 2026-09-23), e a NF
        // não foi marcada — não depende mais do reembolso.
        if (
            (refunded && NeedsCaseReconcile(rma)) ||
            ((rma.inspection_issue === true || rma.inspection_destination === 'DESCARTE') && !rma.nf_loss_at)
        ) {
            return {};
        }
        return null;
    }
    // Supervisor: só os pontos de intervenção dele.
    const due = dueInfo(NearestDue(rma));
    if (['EM_MEDIACAO', 'RECONTATO'].includes(rma.mediation_status) && due?.breached) {
        return { urgent: true };
    }
    if (FAILED_RETURN.includes(rma.return_shipment_status) && destinationOurs && !rma.refund_reconciled_at) {
        return { urgent: true };
    }
    if (refunded && NeedsCaseReconcile(rma)) {
        return {};
    }
    return null;
}

// ── Linguagem visual do módulo (2026-08-27) ─────────────────────────────────
// Reta e plana: cantos QUADRADOS, negrito só na identidade e na ação, e a COR
// como significado — a barra à esquerda diz qual processo pede atenção:
//   vermelho = urgente/estourado · azul = Mercado Livre · âmbar = galpão ·
//   violeta = fabricante · ciano = financeiro · cinza = concluído.

/** Manchete do card: Pack (se houver) ou Venda + nº da reclamação. */
function CardIdentity(rma) {
    const pack = rma.pack_id && rma.pack_id !== 'null' ? rma.pack_id : null;
    const sale = pack ? `Pack ${pack}` : rma.external_order_id ? `Venda ${rma.external_order_id}` : null;
    const claim = rma.external_claim_id ? `#${rma.external_claim_id}` : null;
    if (!sale && !claim) {
        return rmaIdentifier(rma);
    }
    return [sale, claim].filter(Boolean).join('  ·  ');
}

/** Cor do processo dominante (o primeiro trilho pendente). */
function RowAccent(rma, hot) {
    if (hot) {
        return 'border-l-danger-600';
    }
    const tracks = ComputeTracks(rma);
    if (!tracks.ml.na && !tracks.ml.done) {
        return 'border-l-sky-500';
    }
    if (!tracks.fisico.na && !tracks.fisico.done) {
        return 'border-l-amber-500';
    }
    if (!tracks.fabricante.na && !tracks.fabricante.done) {
        return 'border-l-violet-500';
    }
    if (!tracks.financeiro.na && !tracks.financeiro.done) {
        return 'border-l-cyan-600';
    }
    return 'border-l-mono-300 dark:border-l-mono-700';
}

/**
 * Linha da lista: 3 andares enxutos com SÓ o que decide —
 *   1. IDENTIDADE em negrito (Pack/Venda · nº do caso) + prazo à direita
 *   2. a situação em uma frase (peso normal)
 *   3. produto em segundo plano (tipo · SKU · título · qtd) + trilhos
 */
const OccurrenceRow = ({ rma, urgent = false, onOpen, sub = false }) => {
    const due = dueInfo(NearestDue(rma));
    const hot = Boolean(urgent || due?.breached);
    return (
        <button
            type="button"
            onClick={() => onOpen(rma.id)}
            className={`flex w-full flex-col gap-0.5 border-l-4 px-4 py-2 text-left transition-colors hover:bg-mono-50 dark:hover:bg-mono-900/40 ${RowAccent(rma, hot)} ${
                sub
                    ? 'bg-white dark:bg-mono-950'
                    : 'border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950'
            }`}
        >
            <span className="flex w-full items-baseline justify-between gap-3">
                <span className="truncate font-mono text-[13px] font-bold text-black dark:text-white">
                    {CardIdentity(rma)}
                </span>
                {due ? (
                    <span className={`shrink-0 text-[11px] font-semibold ${due.tone}`}>{due.label}</span>
                ) : (
                    <span className="shrink-0 text-[11px] text-mono-400 dark:text-mono-600">
                        {fmtShortDate(rma.claim_date_created || rma.created_at)}
                    </span>
                )}
            </span>
            <span className="truncate text-[13px] text-mono-800 dark:text-mono-200">{SituationLine(rma)}</span>
            <span className="flex w-full items-center justify-between gap-3">
                <span className="min-w-0 truncate text-[11px] text-mono-400 dark:text-mono-500">
                    {[
                        rma.claim_type ? MlLabel(ML_CLAIM_TYPE_LABELS, rma.claim_type) : null,
                        rma.sku,
                        (rma.item_title || '').slice(0, 48),
                        rma.quantity > 1 ? `${rma.quantity} un` : null,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </span>
                <span className="shrink-0">
                    <TrackChips rma={rma} />
                </span>
            </span>
        </button>
    );
};

function fmtShortDate(iso) {
    if (!iso) {
        return '';
    }
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Linha de "Inspecionadas": identidade em negrito + resultado da bancada; o
 * acento à esquerda é o veredito (vermelho = não confere, âmbar = avaria,
 * verde = ok).
 */
const InspectedRow = ({ rma, onOpen }) => (
    <button
        type="button"
        onClick={() => onOpen(rma.id)}
        className={`flex w-full flex-col gap-1 border border-l-4 border-mono-200 bg-white px-4 py-2 text-left transition-colors hover:bg-mono-50 dark:border-mono-800 dark:bg-mono-950 dark:hover:bg-mono-900/40 ${
            rma.inspection_issue
                ? 'border-l-danger-600'
                : rma.inspection_damage
                  ? 'border-l-amber-500'
                  : 'border-l-emerald-500'
        }`}
    >
        <span className="flex w-full items-baseline justify-between gap-3">
            <span className="truncate font-mono text-[13px] font-bold text-black dark:text-white">
                {CardIdentity(rma)}
            </span>
            <span className="shrink-0 text-[11px] text-mono-400 dark:text-mono-600">
                inspecionada {fmtDate(rma.inspected_at)}
            </span>
        </span>
        <span className="flex w-full flex-wrap items-center gap-1.5">
            <span
                className={`inline-flex px-1.5 py-0.5 text-[11px] font-semibold ${
                    rma.inspection_issue
                        ? 'bg-danger-50 text-danger-700 dark:bg-danger-400/10 dark:text-danger-300'
                        : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'
                }`}
            >
                {rma.inspection_issue ? '✗ não confere' : '✓ confere'}
            </span>
            <span
                className={`inline-flex px-1.5 py-0.5 text-[11px] font-semibold ${
                    rma.inspection_damage
                        ? 'bg-warning-50 text-warning-800 dark:bg-warning-400/10 dark:text-warning-300'
                        : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'
                }`}
            >
                {rma.inspection_damage ? '⚠ com avaria' : '✓ íntegra'}
            </span>
            {rma.serial_number && (
                <span className="inline-flex bg-mono-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-mono-600 dark:bg-mono-900 dark:text-mono-300">
                    série {rma.serial_number}
                </span>
            )}
            <span className="inline-flex px-1.5 py-0.5 text-[11px] text-mono-500 dark:text-mono-400">
                {rma.status === 'INSPECIONADO'
                    ? 'aguardando destinação'
                    : rma.resolution
                      ? RESOLUTION_LABELS[rma.resolution] || rma.resolution
                      : rma.status === 'CANCELADO'
                        ? 'encerrada'
                        : rma.status}
            </span>
            <span className="ml-auto truncate text-[11px] text-mono-400 dark:text-mono-500">
                {[rma.sku, (rma.item_title || '').slice(0, 40)].filter(Boolean).join(' · ')}
            </span>
        </span>
    </button>
);

/** Pedido com N ocorrências: primeira linha + "+N do mesmo pedido" expansível. */
const OccurrenceGroup = ({ items, urgentIds, onOpen }) => {
    const [expanded, setExpanded] = useState(false);
    if (items.length === 1) {
        return <OccurrenceRow rma={items[0]} urgent={urgentIds?.has(items[0].id)} onOpen={onOpen} />;
    }
    const [first, ...rest] = items;
    return (
        <div className="border border-mono-200 dark:border-mono-800">
            <OccurrenceRow rma={first} urgent={urgentIds?.has(first.id)} onOpen={onOpen} sub />
            <div className="border-t border-mono-200 dark:border-mono-800">
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="w-full bg-mono-50 px-4 py-1 text-left text-[11px] font-semibold text-mono-500 hover:text-black dark:bg-mono-900 dark:text-mono-400 dark:hover:text-white"
                >
                    {expanded ? '▾' : '▸'} +{rest.length} do mesmo pedido
                </button>
                {expanded &&
                    rest.map((rma) => (
                        <div key={rma.id} className="border-t border-mono-200 dark:border-mono-800">
                            <OccurrenceRow rma={rma} urgent={urgentIds?.has(rma.id)} onOpen={onOpen} sub />
                        </div>
                    ))}
            </div>
        </div>
    );
};

// ── Estação de inspeção ──────────────────────────────────────────────────────
// Bipe + assistente etapa-por-etapa vivem em components/rma/inspectionStation.jsx
// (extraídos em 2026-09-22 para a Gestão de Pedidos reutilizar). O takeover em
// tela cheia continua aqui: stationOpen + inspectingRef + Esc guardado.

// ── Modal: novo RMA manual ───────────────────────────────────────────────────
const CreateRmaModal = ({ isOpen, onClose, onCreated, toast }) => {
    const [form, setForm] = useState({
        sku: '',
        item_title: '',
        quantity: 1,
        external_order_id: '',
        reason_detail: '',
    });
    const [saving, setSaving] = useState(false);
    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.createRma({
                sku: form.sku.trim(),
                item_title: form.item_title.trim() || undefined,
                quantity: Number(form.quantity) || 1,
                external_order_id: form.external_order_id.trim() || undefined,
                reason_detail: form.reason_detail.trim() || undefined,
            });
            toast.success('Ocorrência criada.');
            onCreated();
            onClose();
        } catch (err) {
            toast.error(err.message || 'Não foi possível criar a ocorrência.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Nova ocorrência manual" size="md">
            <form onSubmit={submit} className="flex flex-col gap-3">
                <Input label="SKU *" value={form.sku} onChange={set('sku')} required />
                <Input label="Título do produto" value={form.item_title} onChange={set('item_title')} />
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Quantidade" type="number" min="1" value={form.quantity} onChange={set('quantity')} />
                    <Input
                        label="Pedido ML (opcional)"
                        value={form.external_order_id}
                        onChange={set('external_order_id')}
                    />
                </div>
                <Textarea label="Motivo" value={form.reason_detail} onChange={set('reason_detail')} rows={2} />
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button type="submit" loading={saving} disabled={!form.sku.trim()}>
                        Criar RMA
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

// ── Etiquetas (fila da logística) ────────────────────────────────────────────
const LabelsView = ({ toast, onNavigate }) => {
    const { checkPermission } = useAuth();
    // Fluxograma 2026-08-08: a logística IMPRIME e cola (rmas:logistics:update);
    // o atendimento ASSOCIA a etiqueta à venda (rmas:mediation:update).
    const canPrintLabels = checkPermission('rmas:logistics:update');
    const canAssociateLabels = checkPermission('rmas:mediation:update');
    const [labels, setLabels] = useState(null);
    const [count, setCount] = useState(5);
    const [busy, setBusy] = useState(false);
    const [associating, setAssociating] = useState(null);
    const [saleQuery, setSaleQuery] = useState('');
    const [saleResults, setSaleResults] = useState(null);

    const loadLabels = useCallback(async () => {
        try {
            const data = await api.getRmaLabels({ status: 'GERADA' });
            setLabels(data.labels || []);
        } catch (err) {
            toast.error(err.message || 'Não foi possível carregar as etiquetas.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadLabels();
    }, [loadLabels]);

    const generate = async () => {
        setBusy(true);
        try {
            const data = await api.generateRmaLabels({ count: Number(count) || 1 });
            toast.success(`${data.labels.length} etiqueta(s) gerada(s).`);
            PrintRmaLabels(data.labels, { toastError: toast.error });
            await loadLabels();
        } catch (err) {
            toast.error(err.message || 'Não foi possível gerar etiquetas.');
        } finally {
            setBusy(false);
        }
    };

    const searchSales = async () => {
        if (saleQuery.trim().length < 2) {
            return;
        }
        try {
            const data = await api.searchRmaLabelSales(saleQuery.trim());
            setSaleResults(data.sales || []);
        } catch (err) {
            toast.error(err.message || 'Busca falhou.');
        }
    };

    const associate = async (sale) => {
        setBusy(true);
        try {
            const data = await api.associateRmaLabel(associating.id, sale.external_order_id);
            toast.success(`Etiqueta ${associating.code} associada.`);
            setAssociating(null);
            setSaleQuery('');
            setSaleResults(null);
            await loadLabels();
            onNavigate(data.rma.id);
        } catch (err) {
            toast.error(err.message || 'Não foi possível associar.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-3 border border-mono-200 bg-white p-5 shadow-sm-mono dark:border-mono-800 dark:bg-mono-950">
                {canPrintLabels && (
                    <>
                        <div className="w-28">
                            <Input
                                label="Quantidade"
                                type="number"
                                min="1"
                                max="50"
                                value={count}
                                onChange={(e) => setCount(e.target.value)}
                            />
                        </div>
                        <Button onClick={generate} loading={busy}>
                            Gerar e imprimir
                        </Button>
                    </>
                )}
                <p className="w-full text-xs leading-relaxed text-mono-500 md:w-64 md:flex-1">
                    Cole uma etiqueta em cada mercadoria que chegar e associe-a a uma{' '}
                    <strong>venda do ML que consta em devolução</strong> — o código passa a identificar a unidade.
                    Depois da associação, imprima ou reimprima as etiquetas pelo detalhe da devolução.
                </p>
            </div>

            <div className="flex flex-col gap-2">
                {(labels || []).map((label) => (
                    <div
                        key={label.id}
                        className="flex flex-wrap items-center gap-3 border border-mono-200 bg-white px-4 py-3 dark:border-mono-800 dark:bg-mono-950"
                    >
                        <span className="font-mono text-lg font-bold tracking-wide">{label.code}</span>
                        <span className="text-xs text-mono-400">
                            gerada {new Date(label.created_at).toLocaleString('pt-BR')}
                        </span>
                        <div className="ml-auto flex gap-2">
                            <Button
                                size="sm"
                                variant="ghost"
                                disabled={!canPrintLabels}
                                onClick={() => PrintRmaLabels([label], { toastError: toast.error })}
                            >
                                Reimprimir
                            </Button>
                            {canAssociateLabels && (
                                <Button size="sm" onClick={() => setAssociating(label)}>
                                    Associar à venda
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
                {labels && labels.length === 0 && (
                    <p className="py-10 text-center text-sm text-mono-500">
                        Nenhuma etiqueta aguardando associação. ✨
                    </p>
                )}
            </div>

            {associating && (
                <Modal
                    isOpen
                    onClose={() => {
                        setAssociating(null);
                        setSaleQuery('');
                        setSaleResults(null);
                    }}
                    title={`Associar ${associating.code} à venda`}
                    size="md"
                >
                    <div className="flex flex-col gap-3">
                        <div className="flex items-end gap-2">
                            <div className="flex-1">
                                <Input
                                    label="Buscar venda em devolução (nº do pedido ou comprador)"
                                    value={saleQuery}
                                    onChange={(e) => setSaleQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && searchSales()}
                                />
                            </div>
                            <Button variant="secondary" onClick={searchSales} disabled={saleQuery.trim().length < 2}>
                                Buscar
                            </Button>
                        </div>
                        <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
                            {(saleResults || []).map((sale, i) => (
                                <button
                                    key={`${sale.external_order_id}-${i}`}
                                    type="button"
                                    onClick={() => associate(sale)}
                                    disabled={busy}
                                    className=" border border-mono-200 p-3 text-left transition-colors hover:border-black dark:border-mono-800 dark:hover:border-white"
                                >
                                    <p className="font-mono text-sm font-semibold">{sale.external_order_id}</p>
                                    <p className="truncate text-xs text-mono-500">
                                        {sale.item_title || sale.sku || 'sem item'} ·{' '}
                                        {sale.buyer_nickname || 'comprador'} ·{' '}
                                        {new Date(sale.date_created).toLocaleDateString('pt-BR')}
                                    </p>
                                    <p className="mt-1 text-xs">
                                        <Badge variant="info">
                                            Em devolução ({STATUS_META[sale.rma_status]?.label || sale.rma_status})
                                        </Badge>
                                    </p>
                                </button>
                            ))}
                            {saleResults && saleResults.length === 0 && (
                                <p className="py-6 text-center text-sm text-mono-500">
                                    Nenhuma venda <strong>em devolução</strong> encontrada — só vendas com RMA aberto
                                    podem receber etiqueta.
                                </p>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

// ── Página ───────────────────────────────────────────────────────────────────
const PAGE_SIZE = 50;
const EMPTY_FILTERS = { status: '', mediation: '', refund: '', resolution: '' };

const Rmas = ({ initialQueue = null, initialRole = null }) => {
    const toast = useToast();
    const navigate = useNavigate();
    const { checkPermission } = useAuth();
    // initialRole/initialQueue (rotas pré-filtradas): entra direto na visão
    // "Tudo" com a fila aplicada; o /rmas normal cai na Caixa de entrada.
    const [role, setRole] = useState(() => initialRole || localStorage.getItem('rma_operator_role') || 'supervisor');
    const [view, setView] = useState(initialQueue ? 'tudo' : 'caixa');
    // Sub-aba de cada visão: reclamações/mediações (abertas|encerradas|todas)
    // e devoluções (estacao|EM_TRANSITO|RECEBIDO|INSPECIONADO|todas|etiquetas).
    const [sub, setSub] = useState('abertas');
    const [queue, setQueue] = useState(initialQueue || 'todos'); // fila opcional na visão "Tudo"
    const [processOpen, setProcessOpen] = useState(false);
    const [tasks, setTasks] = useState(null);
    const [inbox, setInbox] = useState(null); // {rows, truncated} mesclado (caixa+aguardando)
    const [list, setList] = useState(null); // resultado do servidor ("tudo")
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [debouncedQ, setDebouncedQ] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [page, setPage] = useState(0);
    const [createOpen, setCreateOpen] = useState(false);
    // Estação de bipe: modo dedicado em TELA CHEIA, aberto pelo botão
    // "Iniciar inspeção"; Esc sai.
    const [stationOpen, setStationOpen] = useState(false);
    const inspectingRef = useRef(false);
    // A mescla da Caixa carrega UMA vez em qualquer view (o menu do topo
    // mostra a contagem) e sempre na view caixa — via ref para não criar
    // loop de dependência com o próprio estado.
    const inboxLoadedRef = useRef(false);
    useEffect(() => {
        if (!stationOpen) {
            return undefined;
        }
        const onKey = (e) => {
            if (e.key === 'Escape' && !inspectingRef.current) {
                setStationOpen(false);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [stationOpen]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQ(q), 300);
        return () => clearTimeout(timer);
    }, [q]);

    const activeQueueDef = QUEUE_DEFS.find((d) => d.key === queue) || null;

    const buildQuery = useCallback(() => {
        const base = {
            source: DEMO_SOURCE || undefined,
            q: debouncedQ || undefined,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
        };
        // Abas por tipo de caso (2026-08-26): universo no servidor via `kind`.
        if (view === 'reclamacoes') {
            return {
                ...base,
                kind: 'reclamacao',
                case_open: sub === 'abertas' ? 'true' : sub === 'encerradas' ? 'false' : undefined,
                order_by: sub === 'abertas' ? 'mediation_due' : 'recent',
            };
        }
        if (view === 'mediacoes') {
            return {
                ...base,
                kind: 'mediacao',
                mediation_status:
                    sub === 'ativas'
                        ? 'EM_MEDIACAO,RECONTATO'
                        : sub === 'encerradas'
                          ? 'GANHA,PERDIDA,COBERTA,ENCERRADA'
                          : undefined,
                order_by: sub === 'ativas' ? 'mediation_due' : 'recent',
            };
        }
        if (view === 'devolucoes') {
            // Sub-página "Inspecionadas": histórico da bancada, mais recente
            // primeiro (independe do estado atual do caso).
            if (sub === 'INSPECIONADAS') {
                return { ...base, kind: 'devolucao', inspected: 'true', order_by: 'inspected_at' };
            }
            // Entregue ≠ a caminho: o ML confirmou a entrega, falta a Logística
            // etiquetar (= receber) — são sub-abas separadas.
            if (sub === 'ENTREGUES') {
                return {
                    ...base,
                    kind: 'devolucao',
                    status: 'EM_TRANSITO',
                    return_delivered: 'true',
                    order_by: 'recent',
                };
            }
            return {
                ...base,
                kind: 'devolucao',
                status: ['EM_TRANSITO', 'RECEBIDO', 'INSPECIONADO'].includes(sub) ? sub : undefined,
                return_delivered: sub === 'EM_TRANSITO' ? 'false' : undefined,
                order_by: 'recent',
            };
        }
        if (activeQueueDef?.filter) {
            return { ...activeQueueDef.filter, ...base };
        }
        return {
            ...base,
            status: filters.status || undefined,
            mediation_status: filters.mediation || undefined,
            refund_status: filters.refund === 'A_CONCILIAR' ? undefined : filters.refund || undefined,
            refund_pending_reconcile: filters.refund === 'A_CONCILIAR' ? 'true' : undefined,
            resolution: filters.resolution || undefined,
            order_by: 'recent',
        };
    }, [activeQueueDef, filters, debouncedQ, page, view, sub]);

    // Caixa/Aguardando: 5 consultas pequenas mescladas (dedupe por id) cobrem
    // as filas de ação de TODOS os papéis; a classificação é client-side
    // (InboxReason) sobre os mesmos campos que alimentam as filas.
    const loadInbox = useCallback(async () => {
        const queries = [
            // Duas páginas do pipeline aberto (o teto do servidor é 200/página).
            { status: OPEN_PIPELINE.join(','), limit: 200 },
            { status: OPEN_PIPELINE.join(','), limit: 200, offset: 200, continuation: true },
            { refund_pending_reconcile: 'true', limit: 200 },
            { nf_loss_pending: 'true', limit: 200 },
            { supplier_concluded: 'false', limit: 200 },
            { return_failed: 'true', limit: 200 },
            // Caso FECHADO mantém janela viva (recontato, revisão, ação com
            // prazo) — sem estas consultas ele contava no badge e sumia da
            // Caixa (bug da revisão 2026-08-27).
            { recontact_pending: 'true', limit: 200 },
            { pending_response: 'true', limit: 200 },
            { review_pending: 'true', limit: 200 },
        ];
        const results = await Promise.all(
            queries.map(({ continuation: _c, ...query }) =>
                api.getRmas({ ...query, source: DEMO_SOURCE || undefined }),
            ),
        );
        const byId = new Map();
        let truncated = false;
        for (let i = 0; i < results.length; i += 1) {
            const rows = results[i].rmas || [];
            // Bateu no teto da página = existe backlog além do carregado; o
            // contador da caixa ganha um "+" em vez de mentir um total menor.
            // Página CONTINUADA por outra consulta (offset seguinte) delega o
            // veredito a ela — senão 201-400 linhas viravam falso "N+".
            const coveredTo = (queries[i].offset || 0) + rows.length;
            const continuedByNext = queries.some(
                (q, j) => j !== i && q.status === queries[i].status && (q.offset || 0) === coveredTo,
            );
            if (rows.length >= (queries[i].limit || 0) && results[i].total > coveredTo && !continuedByNext) {
                truncated = true;
            }
            for (const rma of rows) {
                if (!byId.has(rma.id)) {
                    byId.set(rma.id, rma);
                }
            }
        }
        return { rows: [...byId.values()], truncated };
    }, []);

    const load = useCallback(
        async ({ silent = false } = {}) => {
            if (!silent) {
                setLoading(true);
            }
            try {
                const serverList =
                    view === 'tudo' ||
                    view === 'reclamacoes' ||
                    view === 'mediacoes' ||
                    (view === 'devolucoes' && !['estacao', 'etiquetas'].includes(sub));
                const [tasksData, listData, inboxRows] = await Promise.all([
                    api.getRmaTasks(DEMO_SOURCE ? { source: DEMO_SOURCE } : {}),
                    serverList ? api.getRmas(buildQuery()) : Promise.resolve(null),
                    view === 'caixa' || !inboxLoadedRef.current ? loadInbox() : Promise.resolve(null),
                ]);
                setTasks(tasksData);
                if (listData) {
                    setList(listData);
                }
                if (inboxRows) {
                    setInbox(inboxRows);
                    inboxLoadedRef.current = true;
                }
            } catch (err) {
                if (!silent) {
                    toast.error(err.message || 'Não foi possível carregar as ocorrências.');
                }
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [buildQuery, loadInbox, view, sub],
    );

    useEffect(() => {
        load();
    }, [load]);

    const changeRole = (next) => {
        setRole(next);
        localStorage.setItem('rma_operator_role', next);
        setPage(0);
    };

    const SUB_DEFAULTS = { reclamacoes: 'abertas', mediacoes: 'ativas', devolucoes: 'todas' };
    const changeView = (next) => {
        setView(next);
        setSub(SUB_DEFAULTS[next] || 'todas');
        setQueue('todos');
        setFilters(EMPTY_FILTERS);
        setQ('');
        setDebouncedQ('');
        setPage(0);
    };
    const changeSub = (next) => {
        setSub(next);
        setPage(0);
    };

    // Clique no BPM/etapa: abre "Tudo" já filtrado pela fila da etapa.
    const openQueueInTudo = (key) => {
        const def = QUEUE_DEFS.find((d) => d.key === key);
        if (key === 'etiquetas' || def?.tab === 'etiquetas') {
            // Etiquetas moraram em view própria; hoje vivem em Devoluções.
            setView('devolucoes');
            setSub('etiquetas');
            setPage(0);
            return;
        }
        setView('tudo');
        setFilters(EMPTY_FILTERS);
        setQ('');
        setDebouncedQ('');
        setPage(0);
        setQueue(def ? key : 'todos');
    };

    // ── Classificação da caixa (client-side, memoizada) ──
    const inboxView = useMemo(() => {
        if (!inbox) {
            return null;
        }
        const inboxRows = inbox.rows;
        const term = debouncedQ.trim().toLowerCase();
        const matches = (rma) =>
            !term ||
            [rma.item_title, rma.sku, rma.external_order_id, rma.external_claim_id, rma.buyer_nickname, rma.pack_id]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(term));
        const caixa = [];
        const aguardando = [];
        const urgentIds = new Set();
        for (const rma of inboxRows) {
            const reason = InboxReason(rma, role);
            if (reason) {
                if (matches(rma)) {
                    caixa.push(rma);
                    if (reason.urgent) {
                        urgentIds.add(rma.id);
                    }
                }
            } else if (OPEN_PIPELINE.includes(rma.status) && matches(rma)) {
                aguardando.push(rma);
            }
        }
        const dueMs = (rma) => {
            const due = NearestDue(rma);
            return due ? due.getTime() : Number.POSITIVE_INFINITY;
        };
        const ageMs = (rma) => new Date(rma.claim_date_created || rma.created_at || 0).getTime();
        // Ordenação fixa: estourado > prazo mais próximo > mais antigo.
        caixa.sort((a, b) => {
            const breachedA = dueInfo(NearestDue(a))?.breached ? 0 : 1;
            const breachedB = dueInfo(NearestDue(b))?.breached ? 0 : 1;
            if (breachedA !== breachedB) {
                return breachedA - breachedB;
            }
            if (dueMs(a) !== dueMs(b)) {
                return dueMs(a) - dueMs(b);
            }
            return ageMs(a) - ageMs(b);
        });
        aguardando.sort((a, b) => ageMs(a) - ageMs(b));
        const DAY = 86_400_000;
        const now = Date.now();
        const bands = [
            { key: 'hoje', label: 'Vence hoje', items: [] },
            { key: 'semana', label: 'Esta semana', items: [] },
            { key: 'depois', label: 'Depois', items: [] },
            { key: 'sem_prazo', label: 'Sem prazo', items: [] },
        ];
        for (const rma of caixa) {
            const due = NearestDue(rma);
            if (due && due.getTime() - now < DAY) {
                bands[0].items.push(rma);
            } else if (due && due.getTime() - now < 7 * DAY) {
                bands[1].items.push(rma);
            } else if (due) {
                bands[2].items.push(rma);
            } else {
                bands[3].items.push(rma);
            }
        }
        return { caixa, aguardando, urgentIds, bands, truncated: inbox.truncated };
    }, [inbox, role, debouncedQ]);

    const groupByOrder = (rows) => {
        const groups = [];
        const byOrder = new Map();
        for (const rma of rows) {
            const key = rma.external_order_id || rma.id;
            if (byOrder.has(key)) {
                byOrder.get(key).push(rma);
            } else {
                const arr = [rma];
                byOrder.set(key, arr);
                groups.push({ key, items: arr });
            }
        }
        return groups;
    };

    // Abrir a partir da caixa carrega a ordem corrente — habilita o
    // "Concluir e próximo" no detalhe.
    const openFromCaixa = (id) => {
        navigate(`/rmas/${id}`, { state: { inboxIds: inboxView?.caixa.map((r) => r.id) || [] } });
    };
    const openRma = (id) => navigate(`/rmas/${id}`);

    // Tiles do supervisor: 1 número por equipe + risco.
    const at = tasks?.atendimento;
    const tiles =
        role === 'supervisor' && at
            ? [
                  {
                      key: 'atendimento',
                      accent: 'border-l-sky-500',
                      label: 'Atendimento',
                      count:
                          (at.reclamacoes_responder || 0) +
                          (at.mediacoes_responder || 0) +
                          (at.recontatos_responder || 0) +
                          (at.revisar_devolucao || 0) +
                          (at.para_inspecionar || 0),
                      risk: tasks?.supervisor?.mediacoes_estouradas || 0,
                      role: 'atendimento',
                  },
                  {
                      key: 'logistica',
                      accent: 'border-l-amber-500',
                      label: 'Logística',
                      count: (tasks?.logistica?.para_receber || 0) + (tasks?.logistica?.voltar_estoque || 0),
                      risk: 0,
                      role: 'logistica',
                  },
                  {
                      key: 'financeiro',
                      accent: 'border-l-cyan-600',
                      label: 'Financeiro',
                      count: (tasks?.financeiro?.conferir_reembolso || 0) + (tasks?.financeiro?.nf_perda_pendente || 0),
                      risk: 0,
                      role: 'financeiro',
                  },
                  {
                      key: 'mediacoes',
                      accent: 'border-l-danger-600',
                      label: 'Mediações 48h',
                      count: at.mediacoes_vencendo || 0,
                      risk: tasks?.supervisor?.mediacoes_estouradas || 0,
                      role: 'supervisor',
                  },
              ]
            : [];

    const renderGroups = (rows, urgentIds, onOpen) =>
        groupByOrder(rows).map((group) => (
            <OccurrenceGroup key={group.key} items={group.items} urgentIds={urgentIds} onOpen={onOpen} />
        ));

    // Lista do servidor + paginação — compartilhada por Tudo e pelas abas de
    // tipo (Reclamações/Mediações/Devoluções).
    const inspectedMode = view === 'devolucoes' && sub === 'INSPECIONADAS';
    const serverListBlock = (
        <>
            <div className="flex flex-col gap-2">
                {inspectedMode
                    ? (list?.rmas || []).map((rma) => <InspectedRow key={rma.id} rma={rma} onOpen={openRma} />)
                    : renderGroups(list?.rmas || [], null, openRma)}
                {(list?.rmas || []).length === 0 && (
                    <div className=" border border-dashed border-mono-300 py-14 text-center dark:border-mono-700">
                        <p className="text-sm font-medium text-mono-600 dark:text-mono-300">Nada por aqui 🎉</p>
                        <p className="mt-1 text-xs text-mono-400">
                            {activeQueueDef ? 'Esta fila está zerada.' : 'Nenhuma ocorrência com esses filtros.'}
                        </p>
                    </div>
                )}
            </div>
            {list && list.total > PAGE_SIZE && (
                <div className="flex items-center justify-between text-sm text-mono-500">
                    <span>
                        {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, list.total)} de {list.total}
                    </span>
                    <div className="flex gap-2">
                        <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                            Anterior
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            disabled={(page + 1) * PAGE_SIZE >= list.total}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            Próxima
                        </Button>
                    </div>
                </div>
            )}
        </>
    );

    // ── Menu principal (blocos, 2026-08-27): número grande + o dado crítico
    // colorido de cada universo. Cores seguem o código do módulo: vermelho =
    // urgência, âmbar = galpão esperando.
    const urgentes = inboxView?.urgentIds?.size || 0;
    const estouradas = tasks?.supervisor?.mediacoes_estouradas || 0;
    // Entregues pelo ML e ainda não recebidas: a Logística etiqueta (= recebe)
    // antes da inspeção (rodada 7).
    const entreguesEtiquetar = tasks?.abas?.devolucoes_entregues || 0;
    const menuTabs = [
        {
            key: 'caixa',
            label: 'Caixa de entrada',
            Icon: TaskDaily,
            big: inboxView ? `${inboxView.caixa.length}${inboxView.truncated ? '+' : ''}` : '…',
            sub: urgentes > 0 ? { text: `${urgentes} urgentes`, tone: 'danger' } : { text: 'para fazer agora' },
        },
        {
            key: 'reclamacoes',
            label: 'Reclamações',
            Icon: WarningCircle,
            big: tasks?.abas?.reclamacoes_abertas ?? '…',
            sub: { text: 'abertas com o comprador' },
        },
        {
            key: 'mediacoes',
            label: 'Mediações',
            Icon: Users,
            big: tasks?.abas?.mediacoes_ativas ?? '…',
            sub:
                estouradas > 0
                    ? { text: `${estouradas} com prazo estourado`, tone: 'danger' }
                    : { text: 'ativas com o mediador' },
        },
        {
            key: 'devolucoes',
            label: 'Devoluções',
            Icon: Package,
            big: tasks?.abas?.devolucoes_andamento ?? '…',
            sub:
                entreguesEtiquetar > 0
                    ? { text: `${entreguesEtiquetar} entregues para etiquetar`, tone: 'warn' }
                    : { text: 'em andamento' },
        },
        {
            key: 'tudo',
            label: 'Tudo',
            Icon: ClipboardText,
            big: null,
            sub: { text: 'busca, filtros e todas as filas' },
        },
    ];

    return (
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4 sm:p-6">
            {/* ── Topo: papel, busca e ações ── */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="w-56">
                    <Select value={role} onChange={(e) => changeRole(e.target.value)}>
                        {Object.entries(OPERATOR_ROLES).map(([value, label]) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="relative ml-auto">
                    <MagnifyingGlass
                        size={15}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mono-400"
                    />
                    <input
                        value={q}
                        onChange={(e) => {
                            setQ(e.target.value);
                            setPage(0);
                        }}
                        placeholder="Buscar caso, pedido, pack, SKU, comprador…"
                        className="w-72 border border-mono-300 bg-white py-1.5 pl-9 pr-3 text-sm outline-none transition-colors focus:border-black dark:border-mono-700 dark:bg-mono-950 dark:focus:border-white"
                    />
                </div>
                {checkPermission('rmas:logistics:update') && (
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                            setView('devolucoes');
                            setSub('etiquetas');
                            setPage(0);
                        }}
                    >
                        <Tag size={16} className="mr-1" /> Etiquetas
                    </Button>
                )}
                {checkPermission('rmas:create') && (
                    <Button size="sm" variant="secondary" onClick={() => setCreateOpen(true)}>
                        <Plus size={16} className="mr-1" /> Nova ocorrência
                    </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => load()}>
                    <ArrowClockwise size={16} />
                </Button>
            </div>

            {/* ── Menu principal: blocos com número grande + dado crítico ── */}
            <div className="grid grid-cols-2 gap-px border border-mono-200 bg-mono-200 dark:border-mono-800 dark:bg-mono-800 sm:grid-cols-5">
                {menuTabs.map((tab) => {
                    const active = view === tab.key;
                    const TabIcon = tab.Icon;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => changeView(tab.key)}
                            className={`flex flex-col gap-1 border-t-[3px] px-4 py-2.5 text-left transition-colors ${
                                active
                                    ? 'border-black bg-white dark:border-white dark:bg-mono-950'
                                    : 'border-transparent bg-white hover:bg-mono-50 dark:bg-mono-950 dark:hover:bg-mono-900'
                            }`}
                        >
                            <span
                                className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${
                                    active ? 'text-black dark:text-white' : 'text-mono-400 dark:text-mono-600'
                                }`}
                            >
                                <TabIcon size={13} /> {tab.label}
                            </span>
                            {tab.big !== null ? (
                                <span
                                    className={`text-xl font-bold leading-none tabular-nums ${
                                        active ? 'text-black dark:text-white' : 'text-mono-600 dark:text-mono-300'
                                    }`}
                                >
                                    {tab.big}
                                </span>
                            ) : (
                                <span className="text-xl leading-none text-mono-300 dark:text-mono-700">—</span>
                            )}
                            <span
                                className={`truncate text-[11px] ${
                                    tab.sub.tone === 'danger'
                                        ? 'font-semibold text-danger-600 dark:text-danger-400'
                                        : tab.sub.tone === 'warn'
                                          ? 'font-semibold text-warning-text dark:text-warning-300'
                                          : 'text-mono-400 dark:text-mono-500'
                                }`}
                            >
                                {tab.sub.text}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* ── Supervisor: 1 número por equipe + processo recolhido ── */}
            {tiles.length > 0 && (
                <div className="flex flex-col gap-2">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {tiles.map((tile) => (
                            <button
                                key={tile.key}
                                type="button"
                                onClick={() => {
                                    changeRole(tile.role);
                                    changeView('caixa');
                                }}
                                className={`flex flex-col gap-0.5 border border-l-4 border-mono-200 bg-white px-4 py-2.5 text-left transition-colors hover:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:hover:border-mono-600 ${tile.accent}`}
                            >
                                <span className="text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                                    {tile.label}
                                </span>
                                <span className="text-xl font-bold tabular-nums text-black dark:text-white">
                                    {tile.count}
                                    {tile.risk > 0 && (
                                        <span className="ml-2 text-xs font-bold text-danger-600 dark:text-danger-400">
                                            {tile.risk} em risco
                                        </span>
                                    )}
                                </span>
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => setProcessOpen((v) => !v)}
                        className="self-start px-1 text-[11px] font-semibold text-mono-500 hover:text-black dark:hover:text-white"
                    >
                        {processOpen ? '▾' : '▸'} Processo do dia
                    </button>
                    {processOpen && tasks && <ProcessFlow tasks={tasks} role={role} onSelect={openQueueInTudo} />}
                </div>
            )}

            {view === 'devolucoes' && sub === 'etiquetas' ? (
                <LabelsView toast={toast} onNavigate={openRma} />
            ) : loading && ((view === 'caixa' && !inboxView) || (view !== 'caixa' && !list)) ? (
                <div className="flex justify-center py-16">
                    <LoadingSpinner />
                </div>
            ) : view === 'caixa' ? (
                <div className="flex flex-col gap-2">
                    {inboxView?.caixa.length === 0 && (
                        <div className=" border border-dashed border-mono-300 py-14 text-center dark:border-mono-700">
                            <p className="text-sm font-medium text-mono-600 dark:text-mono-300">Caixa zerada 🎉</p>
                            <p className="mt-1 text-xs text-mono-400">
                                Nada esperando ação de {OPERATOR_ROLES[role]?.toLowerCase() || role} agora.
                            </p>
                        </div>
                    )}
                    {inboxView?.bands
                        .filter((band) => band.items.length > 0)
                        .map((band) => (
                            <div key={band.key} className="flex flex-col gap-2">
                                <p className="mt-2 px-1 text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                                    {band.label} ({band.items.length})
                                </p>
                                {renderGroups(band.items, inboxView.urgentIds, openFromCaixa)}
                            </div>
                        ))}
                    {(inboxView?.aguardando || []).length > 0 && (
                        <details className="mt-3">
                            <summary className="cursor-pointer px-1 text-[10px] font-bold uppercase tracking-widest text-mono-400 hover:text-mono-600 dark:text-mono-600 dark:hover:text-mono-400">
                                Aguardando terceiros ({inboxView.aguardando.length}) — sem ação sua agora
                            </summary>
                            <div className="mt-2 flex flex-col gap-2">
                                {renderGroups(inboxView.aguardando, null, openRma)}
                            </div>
                        </details>
                    )}
                </div>
            ) : view === 'reclamacoes' || view === 'mediacoes' ? (
                <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap gap-2">
                        {(view === 'reclamacoes'
                            ? [
                                  ['abertas', 'Abertas', tasks?.abas?.reclamacoes_abertas],
                                  ['encerradas', 'Encerradas', null],
                                  ['todas', 'Todas', null],
                              ]
                            : [
                                  ['ativas', 'Ativas', tasks?.abas?.mediacoes_ativas],
                                  ['encerradas', 'Encerradas', null],
                                  ['todas', 'Todas', null],
                              ]
                        ).map(([key, label, count]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => changeSub(key)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold transition-colors ${
                                    sub === key
                                        ? 'bg-black text-white dark:bg-white dark:text-black'
                                        : 'bg-white text-mono-600 ring-1 ring-inset ring-mono-200 hover:bg-mono-100 dark:bg-mono-950 dark:text-mono-300 dark:ring-mono-800 dark:hover:bg-mono-900'
                                }`}
                            >
                                {label}
                                {count !== null && count !== undefined && (
                                    <span className="tabular-nums opacity-60">{count}</span>
                                )}
                            </button>
                        ))}
                        {list ? <span className="ml-auto text-sm text-mono-400">{list.total} casos</span> : null}
                    </div>
                    {serverListBlock}
                </div>
            ) : view === 'devolucoes' ? (
                <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap gap-2">
                        {[
                            ['todas', 'Todas', tasks?.abas?.devolucoes_andamento],
                            ['ENTREGUES', 'Entregues — etiquetar', tasks?.abas?.devolucoes_entregues],
                            ['EM_TRANSITO', 'A caminho', tasks?.abas?.devolucoes_transito],
                            ['RECEBIDO', 'Inspecionar', tasks?.abas?.devolucoes_inspecionar],
                            ['INSPECIONADO', 'Destinar', tasks?.abas?.devolucoes_destinar],
                            ['INSPECIONADAS', 'Inspecionadas', null],
                            ['etiquetas', 'Etiquetas', tasks?.atendimento?.etiquetas_pendentes],
                        ].map(([key, label, count]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => changeSub(key)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold transition-colors ${
                                    sub === key
                                        ? 'bg-black text-white dark:bg-white dark:text-black'
                                        : 'bg-white text-mono-600 ring-1 ring-inset ring-mono-200 hover:bg-mono-100 dark:bg-mono-950 dark:text-mono-300 dark:ring-mono-800 dark:hover:bg-mono-900'
                                }`}
                            >
                                {label}
                                {count !== null && count !== undefined && (
                                    <span className="tabular-nums opacity-60">{count}</span>
                                )}
                            </button>
                        ))}
                        <span className="ml-auto flex items-center gap-3">
                            {list ? <span className="text-sm text-mono-400">{list.total} casos</span> : null}
                            <Button size="sm" onClick={() => setStationOpen(true)}>
                                ▮ Iniciar inspeção
                            </Button>
                        </span>
                    </div>
                    {serverListBlock}
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {/* ── Tudo: filtros + fila opcional (as 20+ filas vivem aqui) ── */}
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="w-56">
                            <Select
                                value={queue}
                                onChange={(e) => {
                                    setQueue(e.target.value);
                                    setPage(0);
                                }}
                            >
                                <option value="todos">Fila: todas as ocorrências</option>
                                {QUEUE_DEFS.filter((d) => !d.tab).map((def) => (
                                    <option key={def.key} value={def.key}>
                                        {def.label}
                                        {tasks?.[def.group]?.[def.key] !== undefined
                                            ? ` (${tasks[def.group][def.key]})`
                                            : ''}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        {!activeQueueDef && (
                            <>
                                <div className="w-40">
                                    <Select
                                        value={filters.status}
                                        onChange={(e) => {
                                            setFilters((f) => ({ ...f, status: e.target.value }));
                                            setPage(0);
                                        }}
                                    >
                                        <option value="">Etapa: todas</option>
                                        {Object.entries(STATUS_META).map(([value, meta]) => (
                                            <option key={value} value={value}>
                                                {meta.label}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="w-48">
                                    <Select
                                        value={filters.mediation}
                                        onChange={(e) => {
                                            setFilters((f) => ({ ...f, mediation: e.target.value }));
                                            setPage(0);
                                        }}
                                    >
                                        <option value="">Mediação: todas</option>
                                        <option value="EM_MEDIACAO">Em mediação</option>
                                        <option value="RECONTATO">Recontato (reaberta)</option>
                                        <option value="GANHA">Ganha</option>
                                        <option value="PERDIDA">Perdida</option>
                                        <option value="COBERTA">Coberta pelo ML</option>
                                        <option value="ENCERRADA">Encerrada</option>
                                        <option value="CASO_ML">Caso com o ML</option>
                                        <option value="SEM_MEDIACAO">Sem mediação</option>
                                    </Select>
                                </div>
                                <div className="w-44">
                                    <Select
                                        value={filters.refund}
                                        onChange={(e) => {
                                            setFilters((f) => ({ ...f, refund: e.target.value }));
                                            setPage(0);
                                        }}
                                    >
                                        <option value="">Reembolso: todos</option>
                                        <option value="A_CONCILIAR">A conciliar</option>
                                        {Object.entries(REFUND_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="w-52">
                                    <Select
                                        value={filters.resolution}
                                        onChange={(e) => {
                                            setFilters((f) => ({ ...f, resolution: e.target.value }));
                                            setPage(0);
                                        }}
                                    >
                                        <option value="">Destinação: todas</option>
                                        {Object.entries(RESOLUTION_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </>
                        )}
                        {list ? <span className="ml-auto text-sm text-mono-400">{list.total} ocorrências</span> : null}
                    </div>
                    {serverListBlock}
                </div>
            )}

            {stationOpen && (
                <div className="fixed inset-0 z-40 flex flex-col bg-mono-50 dark:bg-black">
                    <header className="flex items-center justify-between border-b border-mono-200 bg-white px-6 py-3 dark:border-mono-800 dark:bg-mono-950">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-mono-500 dark:text-mono-400">
                            ▮ Estação de inspeção
                        </h2>
                        <button
                            type="button"
                            onClick={() => setStationOpen(false)}
                            className=" border border-mono-300 px-4 py-1.5 text-sm font-semibold text-mono-600 transition-colors hover:border-black hover:text-black dark:border-mono-700 dark:text-mono-300 dark:hover:border-white dark:hover:text-white"
                        >
                            Sair (Esc)
                        </button>
                    </header>
                    <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-start overflow-y-auto p-6 pt-10">
                        <InspectionStation
                            fullscreen
                            onInspectingChange={(v) => {
                                inspectingRef.current = v;
                            }}
                            toast={toast}
                            onOpenCase={(id) => {
                                setStationOpen(false);
                                openRma(id);
                            }}
                            onChanged={() => load({ silent: true })}
                            canLogistics={checkPermission('rmas:logistics:update')}
                            canInspect={
                                checkPermission('rmas:mediation:update') || checkPermission('rmas:logistics:update')
                            }
                        />
                    </main>
                </div>
            )}

            <CreateRmaModal
                isOpen={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => load({ silent: true })}
                toast={toast}
            />
        </div>
    );
};

export default Rmas;
